/**
 * Licensed Materials - Property of IBM
 * IBM Watson Analytics (C) Copyright IBM Corp. 2018, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import '../stylesheets/slotEditor.scss';
import DndSlotItemHandler from './DndSlotItemHandler';
import FieldDropdown from './FieldDropdown';
import SlotEditorItemFlyout from './SlotEditorItemFlyout';
import FieldItem from './FieldItem';
import SlotItemPlaceholder from './SlotItemPlaceholder';

const MAX_HEIGHT = 500;
/**
 * The SlotEditorItem component is responsible for the User Interface for a single slot data item
 */
class SlotEditorItem extends Component {
	constructor(props) {
		super(props);
		this.state = {
			isFlyoutShown: false,
			isTypeInMode: false
		};
		this.sourceId = this.props.slotDataItemHandler.getDatasourceId();
		this.flyoutTooltipTimer = null;
	}

	componentDidMount() {
		this._initializeDndSupport();
	}

	componentDidUpdate(prevProps) {
		if (prevProps && prevProps.slotDataItemHandler !== this.props.slotDataItemHandler) {
			this.dropSlotItemHandler && this.dropSlotItemHandler.updateDropTarget(this.props.slotDataItemHandler);
		}
	}

	componentWillUnmount() {
		this.dropSlotItemHandler && this.dropSlotItemHandler.destroy();
	}

	/**
	 * A slot item consists of the name, a menu (to launch actions), an itemValues section (for displaying local filters on that item)
	 * and an additional 'dropAfter' region which is used to allow a user to drop after to augment a slots definition.
	 */
	render() {
		return (
			<React.Fragment>
				{this._renderFlyout()}
				{
					this.state.isTypeInMode && !this.props.slotDataItemHandler.isMeasureGroupItem() ?
						this._renderFieldDropdown() :
						<FieldItem
							className="slotEditorItem"
							fieldName={this.props.itemName}
							onKeyPress={ (event) => this.props.a11Y.handleKeyPress(event, this.props.slotDataItemHandler.swapSlotItems.bind(this.props.slotDataItemHandler), /*dropAfter*/false) }
							onKeyDown={ (event) => this.props.a11Y.handleKeyDown(event, { slotDataItemHandler : this.props.slotDataItemHandler }, this._slotItemTypeInOn)}
							onKeyUp={ (event) => this.props.a11Y.handleKeyUp(event, { slotDataItemHandler : this.props.slotDataItemHandler })}
							onMouseEnterCallBack={this._showFlyout.bind(this)}
							onMouseLeaveCallBack={this._hideFlyout.bind(this)}
							onClick={this._slotItemTypeInOn.bind(this)}
							fieldGraphics={this.props.slotDataItemHandler.getGraphicData() ? this.props.slotDataItemHandler.getGraphicData() : undefined}
							showActionMenu={!this.props.slotDataItemHandler.isMeasureGroupItem()}
							onOpenActionMenu={!this.props.slotDataItemHandler.isMeasureGroupItem() ? this._slotActionMenu.bind(this) : undefined}
							filterInfo={this.props.slotDataItemHandler.getFilterStringAndNumber()}
							ref={
								fieldItem => {
									if(fieldItem && fieldItem.getDOMNode) {
										this.domNode = fieldItem.getDOMNode();
									}
								}
							}
						/>
				}
				<div className="slotEditorItem__dropAfter"
					onKeyPress={ (event) => this.props.a11Y.handleKeyPress(event, this.props.slotDataItemHandler.swapSlotItems.bind(this.props.slotDataItemHandler), /*dropAfter*/true) }
					onKeyUp={ (event) => this.props.a11Y.slotHandleKeyUp(event, { slotDataItemHandler: this.props.slotDataItemHandler })}
				>
					<SlotItemPlaceholder
						a11Y={this.props.a11Y}
						indexInSlot={this.props.indexInSlot}
						numberItemsInSlot={this.props.numberItemsInSlot}
						canAcceptMoreItems={this.props.slotDataItemHandler.canAcceptMoreItems()}
						slotDataItemHandler={this.props.slotDataItemHandler}
						dashboardApi={this.props.dashboardApi}
						slotPlaceholderStateModel={this.props.slotPlaceholderStateModel}
						initializeDnd={this._initializeDropAfterSupport.bind(this)}
					/>
				</div>
			</React.Fragment>
		);
	}

	//Drag and Drop Support.
	_initializeDndSupport() {
		return this.props.dndManager.then(
			(dndManager) => {
				if (this.domNode) {
					//Initialize a drop handler for both the item node and the 'after node'.
					//Also initialize the node as a drag source.
					this._dndManager = dndManager;
					this.dropSlotItemHandler = new DndSlotItemHandler({
						domNode: this.domNode,
						className: 'dndHover',
						slotDataItemHandler: this.props.slotDataItemHandler,
						addAfter: false,
						dndManager: dndManager
					});

					//We need to set up a callback function when initializing the drag source so that the drag handler is always up-to-date.
					//(not the one at drag drop initialization time!)
					this.dropSlotItemHandler.initializeDndSource(this._dndManager, this._getSlotDataItemHandler.bind(this), this.props.dashboardApi.getFeature('Icons'), this._hideFlyout);
				}
			}
		);
	}

	/** initializes drag and drop support for placeholder dropzone
	 * that is attached to this item
	 * @param node - domNode that dropzone will be initialized on
	 * @param {Function} [options.onDragEnterExecute] - function to be executed when on Drag Enter event occurs
	 * @param {Function} [options.onDragLeaveExecute] - function to be executed when on Drag Leave event occurs
	 * @param {Function} [options.getDndManager] - a callback to get dndManager
	 */
	_initializeDropAfterSupport = (node, options) => {
		return this.props.dndManager.then(
			(dndManager) => {
				if (node) {
					//Initialize a drop handler 'after node'.
					//Also initialize the node as a drag source.
					this._dndManager = dndManager;
					this.dropSlotItemAfterHandler = new DndSlotItemHandler({
						domNode : node,
						className: 'dndHover',
						slotDataItemHandler: this.props.slotDataItemHandler,
						addAfter: true,
						dndManager: dndManager,
						onDragEnter: options.onDragEnter,
						onDragLeave: options.onDragLeave
					});
					if(options.getDndManager) {
						options.getDndManager(dndManager);
					}
				}
			}
		);
	}

	_getSlotDataItemHandler() {
		return this.props.slotDataItemHandler;
	}

	_slotActionMenu() {
		this.props.slotDataItemHandler.openSlotActionMenu(
			this.domNode,
			{
				attachPoint: '.actionmenu',
				reactToolbar: true,
				preferredVertical: true,
				placement: 'bottom'
			}
		);
	}

	_renderFlyout() {
		/* we dont't want to show tooltip if the item is measure group*/
		const shouldRenderFlyout = this.state.isFlyoutShown && this.domNode && !this.props.slotDataItemHandler.isMeasureGroupItem();
		if ( shouldRenderFlyout ) {
			return (
				<SlotEditorItemFlyout
					domNode={this.domNode}
					dashboardApi={this.props.dashboardApi}
					columnId={this.props.columnId}
					sourceId={this.sourceId}
					onClose={this._hideFlyout}
				/>
			);
		}
	}

	_showFlyout = () => {
		// don't show flyout when doing DND because the mui three flyout somehow mess up the drag object(see defect CADBLW-5243)
		// don't show flyout if slot menu is open
		if (this.flyoutTooltipTimer){
			return;
		}
		this.flyoutTooltipTimer = setTimeout( () => {
			if(!this._isDragging() && !this.props.slotDataItemHandler.isSlotActionMenuOpened()) {
				this.setState({
					isFlyoutShown: !this.state.isTypeInMode
				});
			}
		}, 200);
	}

	_hideFlyout = () => {
		this.setState({ isFlyoutShown: false });
		if (this.flyoutTooltipTimer){
			clearTimeout(this.flyoutTooltipTimer);
			this.flyoutTooltipTimer = null;
		}
	}

	_isDragging = () => {
		return !!(this._dndManager && this._dndManager.dragObject);
	}

	_slotItemTypeInOn = () => {
		this.setState({
			isTypeInMode: true,
			isFlyoutShown: false
		});
	}

	_slotItemTypeInOff = () => {
		this.setState({ isTypeInMode: false });
		this._initializeDndSupport();
	}

	_renderFieldDropdown() {
		if (this.state.isTypeInMode  && !this.props.slotDataItemHandler.isMeasureGroupItem()) {
			return (
				<FieldDropdown
					a11Y={this.props.a11Y}
					open={this.state.isTypeInMode}
					textValue={this.props.itemName}
					dashboardApi={this.props.dashboardApi}
					getDropdownMaxHeight={this.getDropdownMaxHeight}
					onBlur={this._slotItemTypeInOff}
					slotDataItemHandler={this.props.slotDataItemHandler}
					sourceId={this.sourceId}
					replaceSlotItem={true}
				/>
			);
		}
	}

	getDropdownMaxHeight() {
		return MAX_HEIGHT;
	}
}

SlotEditorItem.propTypes = {
	slotPlaceholderStateModel: PropTypes.object.isRequired,
	itemName: PropTypes.string,
	indexInSlot: PropTypes.number.isRequired,
	numberItemsInSlot: PropTypes.number.isRequired,
	itemValues: PropTypes.string,
	dashboardApi: PropTypes.object,
	columnId: PropTypes.string,
	slotDataItemHandler: PropTypes.object.isRequired,
	a11Y: PropTypes.object,
	dndManager: PropTypes.object.isRequired
};

export default SlotEditorItem;



// WEBPACK FOOTER //
// ./src/js/SlotEditorItem.js